import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileText, Loader2, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Card, SectionTitle, Placeholder } from "@/partner/ui";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const VERT = "#3C4D3A";
const CLIENT_STEPS = ["Demande", "Étude du besoin", "Entreprise / Devis", "Validation", "Travaux", "Terminé"];
const STEP_TO_CLIENT_INDEX = {
  "Demande client": 0, "Visite / métrés": 1, "Besoin": 1, "Artisan": 2, "Devis": 2,
  "Accepté": 3, "Travaux": 4, "Facture": 4, "Terminé": 5,
};

export default function ClientSmallWorkPage() {
  const { ref } = useParams();
  const { logout } = useAuth();
  const [sw, setSw] = useState(undefined);

  useEffect(() => {
    api.get(`/client-space/small-works/${ref}`).then(({ data }) => setSw(data)).catch(() => setSw(null));
  }, [ref]);

  if (sw === undefined) return <div className="flex min-h-screen items-center justify-center bg-ivory"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;
  if (!sw) return <Navigate to="/espace-client" replace />;

  const currentIdx = STEP_TO_CLIENT_INDEX[sw.step] ?? 0;
  const docUrl = (docId) => `${process.env.REACT_APP_BACKEND_URL}/api/client-space/small-works/${sw.reference}/documents/${docId}/download`;

  return (
    <div data-testid="client-small-work-page" className="min-h-screen bg-ivory font-sans text-navy">
      <header className="border-b border-[#EDEAE3] bg-[#FEFDF9]">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <Link to="/"><Logo className="h-8 w-auto" /></Link>
          <div className="flex items-center gap-2">
            <span className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white" style={{ backgroundColor: VERT }}>Espace Client</span>
            <button data-testid="client-small-work-logout" onClick={async () => { await logout(); window.location.assign("/acces-phase3"); }} aria-label="Déconnexion" className="rounded-full border border-stone-200 p-2 text-stone-500 hover:text-navy"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-6">
        <Link to="/espace-client" data-testid="client-small-work-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Mes dossiers</Link>
        <Card className="mt-3 p-6">
          <p className="font-mono text-[11px] font-bold" style={{ color: VERT }}>{sw.reference}</p>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight sm:text-4xl">{sw.need || "Petits travaux"}</h1>
          <p className="mt-1 text-sm text-stone-500">{sw.address || "Adresse à confirmer"}</p>
        </Card>

        <Card className="mt-4 p-5" testid="client-small-work-timeline">
          <SectionTitle>Où en est mon dossier</SectionTitle>
          <ol className="mt-2 flex flex-col gap-0">
            {CLIENT_STEPS.map((step, i) => {
              const state = i < currentIdx ? "done" : i === currentIdx ? "current" : "upcoming";
              return (
                <li key={step} data-testid={`client-small-work-step-${i}`} className="flex items-start gap-3 pb-4 last:pb-0">
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${state === "done" ? "text-white" : state === "current" ? "bg-cognac text-white" : "bg-[#F0F2F3] text-stone-400"}`} style={state === "done" ? { backgroundColor: VERT } : {}}>
                    {state === "done" ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <p className={`pt-0.5 text-sm font-semibold ${state === "upcoming" ? "text-stone-400" : "text-navy"}`}>{step}</p>
                </li>
              );
            })}
          </ol>
        </Card>

        <Card className="mt-4 p-5" testid="client-small-work-quote">
          <SectionTitle>Devis</SectionTitle>
          {!sw.quote_amount_ht ? <Placeholder>Votre devis n'est pas encore disponible.</Placeholder> : (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-stone-400" />
              <span>{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(sw.quote_amount_ht)} HT — {sw.quote_status === "accepte" ? "Accepté" : "En attente de votre validation"}</span>
              {sw.quote_file_id && <a href={docUrl(sw.quote_file_id)} target="_blank" rel="noreferrer" data-testid="client-small-work-quote-link" className="font-semibold text-petrol underline">Voir le devis</a>}
            </div>
          )}
        </Card>

        <Card className="mt-4 p-5" testid="client-small-work-invoice">
          <SectionTitle>Facture</SectionTitle>
          {!sw.invoice_amount_ht ? <Placeholder>Aucune facture pour le moment.</Placeholder> : (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-stone-400" />
              <span>{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(sw.invoice_amount_ht)} HT — {sw.invoice_status === "payee" ? "Payée" : "À régler"}</span>
              {sw.invoice_file_id && <a href={docUrl(sw.invoice_file_id)} target="_blank" rel="noreferrer" data-testid="client-small-work-invoice-link" className="font-semibold text-petrol underline">Voir la facture</a>}
            </div>
          )}
        </Card>

        {(sw.photos_before?.length > 0 || sw.photos_after?.length > 0) && (
          <Card className="mt-4 p-5" testid="client-small-work-photos">
            <SectionTitle>Photos</SectionTitle>
            <div className="grid gap-4 sm:grid-cols-2">
              {["before", "after"].map((kind) => (
                <div key={kind}>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">{kind === "before" ? "Avant" : "Après"}</p>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {(kind === "before" ? sw.photos_before : sw.photos_after).map((id) => (
                      <a key={id} href={docUrl(id)} target="_blank" rel="noreferrer" data-testid={`client-small-work-photo-${id}`}>
                        <img src={docUrl(id)} alt="" className="aspect-square rounded-lg border border-stone-200 object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
